import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  TvDiv,
  TvSmall,
} from "@trustvid/design-system";
import AiCampaignPromptComposer from "../components/AiCampaignPromptComposer";
import AiCampaignInstructionsPanel from "../components/AiCampaignInstructionsPanel";
import { inferSmartCampaignDefaults } from "../utils/smartCampaignDraftDefaults";
import {
  getSmartCampaignPlaceholder,
  getSmartCampaignReadyMessage,
  getSmartCampaignLoadingSubmessage,
  resolveSmartCampaignInputMode,
} from "../constants/smartCampaignPromptGuide";
import { SMART_CAMPAIGN_MAX_UPLOAD_FILES } from "../constants/smartCampaignUploadLimits";

const SmartCampaignDraftPanel = ({
  prompt = "",
  onPromptChange = () => {},
  onGenerateSmartCampaignDraft = () => {},
  isGeneratingSmartCampaignDraft = false,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [loadingSeconds, setLoadingSeconds] = useState(0);

  const inputMode = resolveSmartCampaignInputMode(prompt, selectedFiles);
  const canGenerate =
    !isGeneratingSmartCampaignDraft
    && inputMode !== "empty";

  useEffect(() => {
    if (!isGeneratingSmartCampaignDraft) {
      setLoadingSeconds(0);
      return undefined;
    }
    const intervalId = setInterval(() => {
      setLoadingSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isGeneratingSmartCampaignDraft]);

  const handleGenerate = () => {
    if (!canGenerate) {
      return;
    }

    const inferred = inferSmartCampaignDefaults({
      prompt,
      files: selectedFiles,
    }) || {};

    onGenerateSmartCampaignDraft({
      prompt: String(prompt || "").trim(),
      files: selectedFiles,
      aspectRatio: inferred.aspectRatio || "16:9",
      title: inferred.title || "",
    });
  };

  return (
    <TvDiv data-testid="smart-campaign-draft-panel">
      <AiCampaignInstructionsPanel activeMode={inputMode} />

      <AiCampaignPromptComposer
        prompt={prompt}
        onPromptChange={onPromptChange}
        files={selectedFiles}
        onFilesChange={setSelectedFiles}
        onSubmit={handleGenerate}
        isSubmitting={isGeneratingSmartCampaignDraft}
        canSubmit={canGenerate}
        fileError={fileError}
        onFileError={setFileError}
        maxFiles={SMART_CAMPAIGN_MAX_UPLOAD_FILES}
        placeholder={getSmartCampaignPlaceholder(inputMode)}
        readyMessage={getSmartCampaignReadyMessage(inputMode, selectedFiles.length)}
        inputMode={inputMode}
      />

      {isGeneratingSmartCampaignDraft ? (
        <TvDiv className="mt-3 text-end" data-testid="smart-campaign-loading-status">
          <TvSmall className="d-block text-muted">
            Designing your campaign…
          </TvSmall>
          <TvSmall className="d-block text-muted">
            {getSmartCampaignLoadingSubmessage(prompt, loadingSeconds)}
          </TvSmall>
        </TvDiv>
      ) : null}
    </TvDiv>
  );
};

SmartCampaignDraftPanel.propTypes = {
  prompt: PropTypes.string,
  onPromptChange: PropTypes.func,
  onGenerateSmartCampaignDraft: PropTypes.func,
  isGeneratingSmartCampaignDraft: PropTypes.bool,
};

export default SmartCampaignDraftPanel;
